import React, { useEffect, useState } from 'react';
import './Content.scss';
import { ContentStatusEnum, PageDetails } from './Session';

export interface ContentProps {
  page: PageDetails | undefined;
  contentStatus: ContentStatusEnum;
}

const Content: React.FC<ContentProps> = ({ page, contentStatus }) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Function to fetch HTML content from the URL
    const fetchHtml = async () => {
      try {
        if (!page?.url) {
          return;
        }
        setIsLoading(true);

        const response = await fetch(page.url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        setHtmlContent(text);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching HTML content:', error);
      }
    };

    if (page?.url && page?.embed) {
      fetchHtml();
    }
  }, [page]); // Empty dependency array means this effect runs once on mount

  useEffect(() => {
    // TypeScript ensures the event type is correctly inferred
    const handleToggleClick = (event: MouseEvent) => {
      // Type assertion to ensure the event target is an element
      const target = event.target as HTMLElement;

      // Find the closest ancestor with the .toggle-button class
      const button = target.closest('.toggle-button') as HTMLElement;

      if (button) {
        const contents = button.nextElementSibling as HTMLElement;

        if (contents.style.height === '' || contents.style.height === '0px') {
          const height = contents.scrollHeight + 'px';
          contents.style.height = height;
          button.textContent = 'Hide ▲';
          button.setAttribute('data-open', 'true');
        } else {
          contents.style.height = '0px';
          button.textContent = 'Show more ▼';
          button.setAttribute('data-open', 'false');
        }
      }
    };

    // Attach the event listener to the document for event delegation
    document.addEventListener('click', handleToggleClick);

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      document.removeEventListener('click', handleToggleClick);
    };
  }, []); // Empty dependency array ensures this runs only once, after initial render

  const contentWindow = () => {
    if (page?.embed) {
      return (
        <div className="iframe">
          <div
            dangerouslySetInnerHTML={{ __html: isLoading ? '' : htmlContent }}
          ></div>
        </div>
      );
    }
    return (
      <iframe
        key={page?.url ?? ''}
        src={isLoading ? '' : (page?.url ?? '')}
        className="iframe"
      ></iframe>
    );
  };

  const getClassName = () => {
    if (!page || isLoading) {
      return 'hidden';
    }
    if (contentStatus === ContentStatusEnum.Hiding) {
      return 'fade';
    }
    if (contentStatus === ContentStatusEnum.Hidden) {
      return 'hidden';
    }
    if (contentStatus === ContentStatusEnum.Showing) {
      return 'fade visible';
    }
    if (contentStatus === ContentStatusEnum.Visible) {
      return 'visible';
    }
    return '';
  };

  return (
    <div id="Content" className={getClassName()}>
      {contentWindow()}
    </div>
  );
};

export default Content;
